import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Headphones } from "lucide-react";

// BrandedAudioPlayer
// ──────────────────
// Minimal branded audio player used on:
//   - the applicant dashboard's "done" screen (preview their stitched MP3)
//   - the public recruiter page when presentation_mode === "audio"
//
// Visual: rounded card, faux waveform bars that animate-fill as the
// audio plays, a circular play/pause button, an elapsed/total timer,
// and a draggable scrubber.
//
// The "waveform" is a generated set of pseudo-random bars seeded from
// the audio URL so it stays stable across renders for the same file.

const formatTime = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// Deterministic seeded waveform bar heights so the design feels intentional
// and doesn't shimmer between renders.
const buildBars = (seed = "", count = 60) => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const bars = [];
  for (let i = 0; i < count; i += 1) {
    h = (h * 1103515245 + 12345) >>> 0;
    const rand = (h % 1000) / 1000; // 0..1
    // Shape the waveform so the middle is taller (feels more "voice")
    const pos = i / (count - 1); // 0..1
    const shape = Math.sin(pos * Math.PI); // 0..1..0
    const v = 0.18 + rand * 0.55 * shape + shape * 0.25;
    bars.push(Math.max(0.08, Math.min(1, v)));
  }
  return bars;
};

const BrandedAudioPlayer = ({
  src,
  durationSeconds = null,
  variant = "default", // "default" | "hero"
  className = "",
  ...rest
}) => {
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds || 0);
  const [ready, setReady] = useState(false);

  const bars = useMemo(() => buildBars(src || "", variant === "hero" ? 80 : 60), [src, variant]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onTime = () => setCurrentTime(el.currentTime || 0);
    const onLoaded = () => {
      setDuration(el.duration || durationSeconds || 0);
      setReady(true);
    };
    const onEnded = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("ended", onEnded);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  }, [src, durationSeconds]);

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el) return;
    try {
      if (playing) el.pause();
      else await el.play();
    } catch (e) {
      console.error("audio play failed", e);
    }
  };

  const handleSeek = (clientX) => {
    const el = audioRef.current;
    const wave = containerRef.current;
    if (!el || !wave || !duration) return;
    const rect = wave.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    el.currentTime = ratio * duration;
    setCurrentTime(el.currentTime);
  };

  const onWaveClick = (e) => handleSeek(e.clientX);

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const isHero = variant === "hero";

  return (
    <div
      className={
        `relative w-full rounded-2xl overflow-hidden shadow-xl shadow-slate-900/10 ${
          isHero
            ? "bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white p-6 sm:p-8"
            : "bg-gradient-to-br from-slate-900 to-blue-900 text-white p-5"
        } ${className}`
      }
      data-testid="branded-audio-player"
      {...rest}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* subtle grain / glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 20% 0%, rgba(59,130,246,0.35), transparent 60%), radial-gradient(ellipse 60% 50% at 90% 100%, rgba(244,114,182,0.18), transparent 60%)",
        }}
      />

      <div className="relative flex items-center gap-2 mb-4">
        <Headphones className="h-4 w-4 text-blue-300" />
        <span className="text-[10px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-blue-200/90">
          Audio Introduction
        </span>
      </div>

      <div className={`relative flex items-center gap-4 sm:gap-6 ${isHero ? "" : ""}`}>
        <button
          type="button"
          onClick={togglePlay}
          disabled={!src}
          className={`relative shrink-0 flex items-center justify-center rounded-full bg-white text-slate-900 hover:bg-blue-50 transition shadow-lg shadow-black/30 ${
            isHero ? "h-16 w-16 sm:h-20 sm:w-20" : "h-12 w-12 sm:h-14 sm:w-14"
          } ${playing ? "ring-4 ring-blue-400/30" : ""}`}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="branded-audio-play-btn"
        >
          {playing ? (
            <Pause className={isHero ? "h-7 w-7" : "h-5 w-5"} />
          ) : (
            <Play className={isHero ? "h-7 w-7 translate-x-[2px]" : "h-5 w-5 translate-x-[1px]"} />
          )}
        </button>

        <div className="flex-1 min-w-0">
          {/* Waveform */}
          <div
            ref={containerRef}
            className="relative cursor-pointer select-none"
            onClick={onWaveClick}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration || 0)}
            aria-valuenow={Math.round(currentTime)}
            data-testid="branded-audio-waveform"
          >
            <div
              className={`flex items-center justify-between w-full ${
                isHero ? "h-14 sm:h-16" : "h-10 sm:h-12"
              }`}
            >
              {bars.map((h, i) => {
                const filled = i / bars.length <= progress;
                return (
                  <span
                    key={i}
                    className={`flex-1 mx-[1px] rounded-full transition-colors duration-150 ${
                      filled ? "bg-blue-300" : "bg-white/25"
                    }`}
                    style={{ height: `${Math.round(h * 100)}%` }}
                  />
                );
              })}
            </div>
            {/* Progress line under bars */}
            <div className="mt-1 h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-300 transition-[width] duration-150"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between text-xs sm:text-sm text-blue-100/90 font-mono tabular-nums">
            <span data-testid="branded-audio-current">
              {formatTime(currentTime)}
            </span>
            <span data-testid="branded-audio-duration">
              {formatTime(duration)}
            </span>
          </div>
          {!ready && src && (
            <p className="text-[10px] uppercase tracking-widest text-blue-300/70 mt-1">
              Loading audio…
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandedAudioPlayer;
